import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play,
  Square,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Zap,
  Eye,
  Search,
  Shield,
  Bell,
  Database,
  ArrowLeft,
  Settings,
  FileText,
  RefreshCw,
  History,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBankerCycles, useBankerCycle } from '../hooks/useBankerCycles';
import type { BankerCycle } from '../types/expert';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

interface StatusEvent {
  step: string;
  detail: string;
  timestamp: number;
}

interface BankerStatus {
  user_id: number;
  subscription_tier: string;
  banker_enabled: boolean;
  eligible: boolean;
  config: {
    banker_provider: string;
    banker_model: string;
    web_search_provider: string;
    max_daily_notifications: number;
  };
  recent_leads: {
    id: number;
    symbol: string;
    direction: string;
    status: string;
    gate_score: number | null;
    created_at: string | null;
  }[];
  last_cycle: {
    id: number;
    status: string;
    trigger: string;
    duration_s: number | null;
    approved: number;
    rejected: number;
    pass1_alerts: number;
    pass2_candidates: number;
    pass2_skip_reason: string | null;
    pass2_error: string | null;
    pass3_candidates: number | null;
    pass3_skip_reason: string | null;
    pass3_error: string | null;
    notifications_sent: number;
    holdings_checked: number;
    markets_open: string | null;
    skip_reason: string | null;
    started_at: string | null;
  } | null;
  last_cycle_health: {
    state: 'healthy' | 'error' | 'idle' | 'running' | 'skipped';
    stage: 'pass1' | 'pass2' | 'pass3' | 'cycle' | null;
    message: string;
  } | null;
  cycles_today: number;
  next_scheduled: string | null;
  stats_7d: {
    total: number;
    success_rate: number | null;
    avg_duration_s: number | null;
  } | null;
}

const STEP_ICONS: Record<string, React.ElementType> = {
  start: Zap,
  init: Settings,
  markets: Activity,
  holdings: Database,
  pass1: Eye,
  pass1_done: CheckCircle2,
  pass1_skip: Clock,
  pass1_error: XCircle,
  pass2: Search,
  pass2_done: CheckCircle2,
  pass2_error: XCircle,
  validation: Shield,
  validation_done: CheckCircle2,
  notify: Bell,
  store: Database,
  complete: CheckCircle2,
  summary: FileText,
  skip: AlertTriangle,
  error: XCircle,
  done: Square,
};

const STEP_COLORS: Record<string, string> = {
  start: 'text-teal-400',
  init: 'text-slate-400',
  markets: 'text-blue-400',
  holdings: 'text-blue-400',
  pass1: 'text-amber-400',
  pass1_done: 'text-emerald-400',
  pass1_skip: 'text-slate-500',
  pass1_error: 'text-red-400',
  pass2: 'text-purple-400',
  pass2_done: 'text-emerald-400',
  pass2_error: 'text-red-400',
  validation: 'text-amber-400',
  validation_done: 'text-emerald-400',
  notify: 'text-teal-400',
  store: 'text-blue-400',
  complete: 'text-emerald-400',
  summary: 'text-slate-400',
  skip: 'text-amber-400',
  error: 'text-red-400',
  done: 'text-slate-500',
};

const STEP_LABELS: Record<string, string> = {
  start: 'Starting',
  init: 'Initializing',
  markets: 'Markets',
  holdings: 'Holdings',
  pass1: 'Pass 1: Position Oversight',
  pass1_done: 'Pass 1 Complete',
  pass1_skip: 'Pass 1 Skipped',
  pass1_error: 'Pass 1 Error',
  pass2: 'Pass 2: Opportunity Discovery',
  pass2_done: 'Pass 2 Complete',
  pass2_error: 'Pass 2 Error',
  validation: 'Validation Gate',
  validation_done: 'Validation Complete',
  notify: 'Notifications',
  store: 'Storing Leads',
  complete: 'Cycle Complete',
  summary: 'Summary',
  skip: 'Skipped',
  error: 'Error',
  done: 'Done',
};

const STATUS_BADGE: Record<string, { color: string; label: string }> = {
  completed: { color: 'bg-emerald-500/10 text-emerald-400', label: 'Completed' },
  partial: { color: 'bg-amber-500/10 text-amber-400', label: 'Partial' },
  failed: { color: 'bg-red-500/10 text-red-400', label: 'Failed' },
  skipped: { color: 'bg-slate-500/10 text-slate-400', label: 'Skipped' },
  running: { color: 'bg-teal-500/10 text-teal-400', label: 'Running' },
};

// ─── Cycle Health Card ────────────────────────────────────────────

const CycleHealthCard: React.FC<{ status: BankerStatus | undefined }> = ({ status }) => {
  if (!status) return null;

  const lc = status.last_cycle;
  const health = status.last_cycle_health;
  const badge = lc ? STATUS_BADGE[lc.status] || STATUS_BADGE.completed : null;

  // M3: stale warning — last cycle > 60 min ago and banker is eligible
  const isStale = status.eligible && lc?.started_at
    ? (Date.now() - new Date(lc.started_at).getTime()) > 60 * 60 * 1000
    : false;

  // Relative time helper
  const relativeTime = (isoStr: string | null) => {
    if (!isoStr) return null;
    const diffMs = Date.now() - new Date(isoStr).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    return `${Math.floor(diffMin / 60)}h ${diffMin % 60}m ago`;
  };

  const s7 = status.stats_7d;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Cycle Health</h2>
        {isStale && (
          <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
            <AlertTriangle size={11} />
            No cycle in 60+ min
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">Last Cycle</div>
          {lc ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded ${badge?.color}`}>
                {badge?.label}
              </span>
              <span className="text-xs text-slate-500">
                {lc.duration_s != null ? `${lc.duration_s}s` : ''}
              </span>
              <span className="text-xs text-slate-600">{relativeTime(lc.started_at)}</span>
            </div>
          ) : (
            <div className="text-sm text-slate-600">None yet</div>
          )}
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Today</div>
          <div className="text-sm font-medium text-white">{status.cycles_today} cycles</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Next Scheduled</div>
          <div className="text-sm font-medium text-slate-300">
            {status.next_scheduled
              ? new Date(status.next_scheduled).toLocaleTimeString()
              : 'N/A'}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Last Approved</div>
          <div className="text-sm font-medium text-emerald-400">{lc?.approved ?? 0}</div>
        </div>
      </div>
      {health && (
        <div className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
          health.state === 'error'
            ? 'border-red-500/30 bg-red-500/10 text-red-300'
            : health.state === 'running'
              ? 'border-teal-500/30 bg-teal-500/10 text-teal-300'
            : health.state === 'skipped'
              ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
              : health.state === 'idle'
                ? 'border-slate-600/60 bg-slate-700/30 text-slate-300'
                : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
        }`}>
          {health.message}
        </div>
      )}
      {/* M4: 7-day stats */}
      {s7 && s7.total > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700/40 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">7-Day Cycles</div>
            <div className="text-sm font-medium text-white">{s7.total}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Success Rate</div>
            <div className={`text-sm font-medium ${
              s7.success_rate != null && s7.success_rate >= 80 ? 'text-emerald-400'
              : s7.success_rate != null && s7.success_rate >= 50 ? 'text-amber-400'
              : 'text-red-400'
            }`}>
              {s7.success_rate != null ? `${s7.success_rate}%` : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Avg Duration</div>
            <div className="text-sm font-medium text-slate-300">
              {s7.avg_duration_s != null ? `${s7.avg_duration_s}s` : '—'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Cycle Row (expandable) ───────────────────────────────────────

const CycleRow: React.FC<{ cycle: BankerCycle }> = ({ cycle }) => {
  const [expanded, setExpanded] = useState(false);
  const { data: detail } = useBankerCycle(expanded ? cycle.id : null);

  const badge = STATUS_BADGE[cycle.status] || STATUS_BADGE.completed;
  const startTime = cycle.started_at ? new Date(cycle.started_at) : null;

  return (
    <div className="border-b border-slate-700/30 last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown size={14} className="text-slate-500 shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-slate-500 shrink-0" />
        )}
        <span className="text-xs text-slate-500 w-32 shrink-0">
          {startTime ? startTime.toLocaleString() : 'Unknown'}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded ${badge.color} shrink-0`}>
          {badge.label}
        </span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          cycle.trigger === 'scheduled' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'
        } shrink-0`}>
          {cycle.trigger}
        </span>
        <span className="flex-1 text-xs text-slate-400 truncate">
          {cycle.skip_reason
            ? cycle.skip_reason
            : `${cycle.approved} approved, ${cycle.rejected} rejected`}
        </span>
        <span className="text-xs text-slate-500 shrink-0">
          {cycle.duration_s != null ? `${cycle.duration_s}s` : ''}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pl-10">
          {/* Cycle metrics */}
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-3">
            <MetricPill label="Holdings" value={cycle.holdings_checked} />
            <MetricPill label="P1 Alerts" value={cycle.pass1_alerts} color={cycle.pass1_error ? 'text-red-400' : undefined} />
            <MetricPill label="P2 Candidates" value={cycle.pass2_candidates} color={cycle.pass2_error ? 'text-red-400' : undefined} />
            <MetricPill label="P3 IPO" value={cycle.pass3_candidates ?? 0} color={cycle.pass3_error ? 'text-red-400' : 'text-purple-400'} />
            <MetricPill label="Approved" value={cycle.approved} color="text-emerald-400" />
            <MetricPill label="Rejected" value={cycle.rejected} color="text-red-400" />
            <MetricPill label="Notified" value={cycle.notifications_sent} color="text-teal-400" />
          </div>
          {cycle.pass3_duration_s != null && (
            <div className="text-xs text-slate-500 mb-2">P3 duration: {cycle.pass3_duration_s}s</div>
          )}

          {cycle.markets_open && (
            <div className="text-xs text-slate-500 mb-2">Markets: {cycle.markets_open}</div>
          )}

          {(cycle.pass1_error || cycle.pass2_error || cycle.pass3_error) && (
            <div className="space-y-1 mb-3">
              {cycle.pass1_error && (
                <div className="text-xs text-red-400 bg-red-500/5 rounded px-2 py-1">
                  Pass 1 Error: {cycle.pass1_error}
                </div>
              )}
              {cycle.pass2_error && (
                <div className="text-xs text-red-400 bg-red-500/5 rounded px-2 py-1">
                  Pass 2 Error: {cycle.pass2_error}
                </div>
              )}
              {cycle.pass3_error && (
                <div className="text-xs text-red-400 bg-red-500/5 rounded px-2 py-1">
                  Pass 3 Error: {cycle.pass3_error}
                </div>
              )}
            </div>
          )}

          {/* Step log */}
          {detail?.step_log && detail.step_log.length > 0 && (
            <div className="bg-slate-900/50 rounded-lg p-3 font-mono text-xs space-y-1 max-h-60 overflow-y-auto">
              {detail.step_log.map((entry, i) => {
                const Icon = STEP_ICONS[entry.step] || Activity;
                const color = STEP_COLORS[entry.step] || 'text-slate-400';
                const label = STEP_LABELS[entry.step] || entry.step;
                const ts = new Date(entry.ts).toLocaleTimeString();

                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-slate-600 w-16 shrink-0">{ts}</span>
                    <Icon size={12} className={`${color} shrink-0 mt-0.5`} />
                    <span className={color}>{label}</span>
                    {entry.detail && <span className="text-slate-500 ml-1">{entry.detail}</span>}
                  </div>
                );
              })}
            </div>
          )}

          {detail && !detail.step_log?.length && (
            <div className="text-xs text-slate-600">No step log available</div>
          )}

          {expanded && !detail && (
            <div className="text-xs text-slate-600 animate-pulse">Loading step log...</div>
          )}
        </div>
      )}
    </div>
  );
};

const MetricPill: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <div className="text-center">
    <div className={`text-sm font-mono font-medium ${color || 'text-white'}`}>{value}</div>
    <div className="text-[10px] text-slate-500">{label}</div>
  </div>
);

// ─── History Tab ──────────────────────────────────────────────────

const HistoryTab: React.FC = () => {
  const [days, setDays] = useState(7);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data, isLoading, refetch } = useBankerCycles({ days, status: statusFilter || undefined, limit: 50 });

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
        >
          <option value={1}>Today</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
        >
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="partial">Partial</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <button
          onClick={() => refetch()}
          className="text-slate-500 hover:text-slate-300 transition-colors ml-auto"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </button>
        {data && (
          <span className="text-xs text-slate-500">{data.total} cycles</span>
        )}
      </div>

      {/* Cycle list */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {isLoading && (
          <div className="text-slate-500 text-sm text-center py-12 animate-pulse">Loading cycles...</div>
        )}
        {!isLoading && data && data.items.length === 0 && (
          <div className="text-slate-600 text-sm text-center py-12">No cycles found in this period</div>
        )}
        {data?.items.map(cycle => (
          <CycleRow key={cycle.id} cycle={cycle} />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────

const BankerMonitor: React.FC = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<'live' | 'history'>('live');
  const [events, setEvents] = useState<StatusEvent[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const logRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery<BankerStatus>({
    queryKey: ['banker-status'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/v1/banker/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch status');
      return res.json();
    },
  });

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const runCycle = useCallback(async () => {
    setEvents([]);
    setIsRunning(true);
    setElapsed(0);
    setActiveView('live');

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/banker/run`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) throw new Error('No response body');

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              setEvents(prev => [...prev, { ...data, timestamp: Date.now() }]);
            } catch {
              // skip parse errors
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setEvents(prev => [...prev, {
          step: 'error',
          detail: err instanceof Error ? err.message : 'Unknown error',
          timestamp: Date.now(),
        }]);
      }
    } finally {
      setIsRunning(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      refetchStatus();
    }
  }, [refetchStatus]);

  const stopCycle = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const lastStep = events.length > 0 ? events[events.length - 1] : null;
  const isComplete = lastStep?.step === 'done' || lastStep?.step === 'complete';
  const hasError = events.some(e => e.step.includes('error'));

  // Parse summary from events
  const summaryEvent = events.find(e => e.step === 'summary');
  let summary: Record<string, unknown> | null = null;
  if (summaryEvent) {
    try {
      summary = JSON.parse(summaryEvent.detail);
    } catch {
      // ignore
    }
  }

  return (
    <PermissionGate permission="feature:banker" fallback={<div className="p-8"><UpgradeNudge tier="premium" /></div>}>
    <div className="min-h-screen bg-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">Investment Banker Monitor</h1>
            <p className="text-slate-400 text-sm mt-0.5">Live monitoring, manual cycle trigger, and execution history</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/trading/banker/settings')}
              className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white text-sm transition-colors flex items-center gap-1.5"
            >
              <Settings size={14} />
              Settings
            </button>
            <button
              onClick={() => navigate('/trading/banker/leads')}
              className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white text-sm transition-colors flex items-center gap-1.5"
            >
              <FileText size={14} />
              Leads
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 mb-6 bg-slate-800/50 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveView('live')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              activeView === 'live'
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Activity size={14} />
            Live
          </button>
          <button
            onClick={() => setActiveView('history')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              activeView === 'history'
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <History size={14} />
            History
          </button>
        </div>

        {/* Status Card */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Status</h2>
            <button
              onClick={() => refetchStatus()}
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              <RefreshCw size={14} className={statusLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {status ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-slate-500 mb-1">Eligible</div>
                <div className={`text-sm font-medium ${status.eligible ? 'text-emerald-400' : 'text-red-400'}`}>
                  {status.eligible ? 'Yes' : 'No'}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Tier</div>
                <div className="text-sm font-medium text-white capitalize">{status.subscription_tier || 'none'}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">LLM</div>
                <div className="text-sm font-medium text-white">{status.config.banker_model}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Search</div>
                <div className="text-sm font-medium text-white capitalize">{status.config.web_search_provider}</div>
              </div>
            </div>
          ) : (
            <div className="text-slate-500 text-sm">Loading...</div>
          )}

          {status?.last_cycle_health?.state === 'error' && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
              <div className="flex items-start gap-2">
                <XCircle size={15} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-red-200">Latest banker cycle hit an engine failure</div>
                  <div>{status.last_cycle_health.message}</div>
                </div>
              </div>
            </div>
          )}

          {status && !status.eligible && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
              <AlertTriangle size={14} className="inline mr-1.5" />
              Banker is disabled. Enable it in Settings.
            </div>
          )}
        </div>

        {/* Cycle Health Card */}
        <CycleHealthCard status={status} />

        {/* Live View */}
        {activeView === 'live' && (
          <>
            {/* Run Button */}
            <div className="flex items-center gap-4 mb-6">
              {!isRunning ? (
                <button
                  onClick={runCycle}
                  disabled={statusLoading}
                  className="px-6 py-3 rounded-xl bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium text-sm transition-colors flex items-center gap-2"
                >
                  <Play size={16} />
                  Run Cycle Now
                </button>
              ) : (
                <button
                  onClick={stopCycle}
                  className="px-6 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 font-medium text-sm transition-colors flex items-center gap-2"
                >
                  <Square size={16} />
                  Stop
                </button>
              )}

              {isRunning && (
                <div className="flex items-center gap-3 text-slate-400 text-sm">
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  Running... {elapsed}s
                </div>
              )}

              {!isRunning && isComplete && !hasError && (
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <CheckCircle2 size={14} />
                  Cycle complete
                </div>
              )}

              {!isRunning && hasError && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <XCircle size={14} />
                  Completed with errors
                </div>
              )}
            </div>

            {/* Summary Card (after completion) */}
            {summary && !isRunning && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 mb-6">
                <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">Cycle Results</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-white">{String(summary.pass1_alerts || 0)}</div>
                    <div className="text-xs text-slate-500">Position Alerts</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{String(summary.pass2_candidates || 0)}</div>
                    <div className="text-xs text-slate-500">Candidates Found</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-400">{String(summary.approved || 0)}</div>
                    <div className="text-xs text-slate-500">Approved</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">{String(summary.rejected || 0)}</div>
                    <div className="text-xs text-slate-500">Rejected</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-300">{String(summary.total_duration_s || 0)}s</div>
                    <div className="text-xs text-slate-500">Duration</div>
                  </div>
                </div>
              </div>
            )}

            {/* Live Activity Log */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-700/50 flex items-center justify-between">
                <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Activity size={14} />
                  Activity Log
                </h2>
                {events.length > 0 && (
                  <span className="text-xs text-slate-500">{events.length} events</span>
                )}
              </div>

              <div
                ref={logRef}
                className="p-4 max-h-[480px] overflow-y-auto font-mono text-sm space-y-1"
              >
                {events.length === 0 && !isRunning && (
                  <div className="text-slate-600 text-center py-12">
                    Press "Run Cycle Now" to trigger the Investment Banker
                  </div>
                )}

                {events.map((event, i) => {
                  const Icon = STEP_ICONS[event.step] || Activity;
                  const color = STEP_COLORS[event.step] || 'text-slate-400';
                  const label = STEP_LABELS[event.step] || event.step;
                  const time = new Date(event.timestamp).toLocaleTimeString();

                  return (
                    <div key={i} className="flex items-start gap-3 py-1.5">
                      <span className="text-slate-600 text-xs w-20 shrink-0 pt-0.5">{time}</span>
                      <Icon size={14} className={`${color} shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <span className={`${color} font-medium`}>{label}</span>
                        {event.detail && event.step !== 'summary' && (
                          <span className="text-slate-400 ml-2">{event.detail}</span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {isRunning && (
                  <div className="flex items-center gap-3 py-1.5 text-slate-500">
                    <span className="w-20 shrink-0" />
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Leads */}
            {status?.recent_leads && status.recent_leads.length > 0 && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 mt-6">
                <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">Recent Leads</h2>
                <div className="space-y-2">
                  {status.recent_leads.map(lead => (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-750 transition-colors"
                      onClick={() => navigate('/trading/banker/leads')}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          lead.direction === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {lead.direction}
                        </span>
                        <span className="text-white font-medium">{lead.symbol}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        {lead.gate_score != null && (
                          <span className={`text-sm font-mono ${
                            lead.gate_score >= 75 ? 'text-emerald-400' :
                            lead.gate_score >= 60 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {lead.gate_score}%
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          lead.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400' :
                          lead.status === 'acted_on' ? 'bg-teal-500/10 text-teal-400' :
                          'bg-red-500/10 text-red-400'
                        }`}>
                          {lead.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* History View */}
        {activeView === 'history' && <HistoryTab />}
      </div>
    </div>
    </PermissionGate>
  );
};

export default BankerMonitor;
